/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { useEffect, useState } from 'react'
import { importsApi } from '../../lib/api.js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { MenuImportView } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { Card, toast } from '../../components/ui/index.js'

// ─── Atividade ao vivo durante o OCR (nunca mais "parece travado") ───────────

// ─── Retomada forçada (processo parado em background) ────────────────────────

/** O import roda em background (corrente self-dispatch + cron após 10min);
 *  este botão re-despacha AGORA e recoloca páginas com erro na fila. Destaca
 *  quando está sem progresso há mais de 2min (> tempo de 1 leitura). */
export function KickButton({ view, staleSec }: { view: MenuImportView; staleSec?: number }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const mut = useMutation({
    mutationFn: () => importsApi.kick(view.id),
    onSuccess: () => {
      toast.success(t('admin.import.kick_done'))
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
    onError: (e: Error) => {
      toast.error(e.message)
      // 409 WAITING_INPUT: o servidor espera o dono — recarrega para a
      // pergunta aparecer em destaque (#300)
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
  })
  const stuck = (staleSec ?? 0) > 120
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={mut.isPending}
        data-testid="import-kick"
        onClick={() => mut.mutate()}
        className={
          stuck
            ? 'rounded-full bg-amber-500 px-3 py-1 text-[11px] font-semibold text-pratzu-aubergine hover:bg-amber-600'
            : 'rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50'
        }
      >
        ⚡ {t('admin.import.kick')}
      </button>
      {stuck && (
        <span className="text-[11px] text-amber-700">
          {t('admin.import.kick_stale_hint', { s: String(staleSec) })}
        </span>
      )}
    </div>
  )
}

export function ProcessingCard({ view }: { view: MenuImportView }) {
  const { t } = useI18n()
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    setElapsed(Math.max(0, Math.round(Date.now() / 1000 - view.created_at)))
    const id = setInterval(() => {
      setElapsed(Math.max(0, Math.round(Date.now() / 1000 - view.created_at)))
    }, 1000)
    return () => clearInterval(id)
  }, [view.created_at])

  const pages = view.pages ?? []
  const pendingCount = Math.max(
    view.page_count - view.processed_pages,
    pages.filter((p) => p.status === 'pending').length,
  )
  const allRead = pendingCount === 0 && view.processed_pages > 0
  const staleSec = Math.max(0, Math.round(Date.now() / 1000 - (view.updated_at ?? view.created_at)))

  // fila de estágios que o pipeline executa (o servidor publica o atual em
  // current_step; o fast-path pula o agente — a leitura limpa dispensa)
  const stepKeys = ['ocr', 'agent', 'semantic', 'done'] as const
  const curIdx = (() => {
    const i = stepKeys.indexOf((view.current_step ?? '') as (typeof stepKeys)[number])
    if (i >= 0) return i
    return allRead ? 1 : 0 // legado sem passo publicado
  })()
  const stepLabel =
    view.current_step === 'agent'
      ? t('admin.import.step_agent')
      : view.current_step === 'semantic'
        ? t('admin.import.step_semantic')
        : allRead
          ? t('admin.import.phase_analyzing')
          : t('admin.import.phase_reading', { n: String(Math.max(pendingCount, 1)) })

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
          {stepLabel}
        </p>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-gray-400 tabular-nums">⏱ {elapsed}s</span>
          <KickButton view={view} staleSec={staleSec} />
        </div>
      </div>

      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        {stepKeys.map((k, i) => {
          const state = i < curIdx ? 'done' : i === curIdx ? 'active' : 'todo'
          return (
            <li key={k} className="flex items-center gap-1.5">
              <span
                className={
                  state === 'done'
                    ? 'text-[11px] font-medium text-emerald-600'
                    : state === 'active'
                      ? 'text-[11px] font-semibold text-gray-900'
                      : 'text-[11px] text-gray-400'
                }
              >
                {state === 'done' ? '✓' : state === 'active' ? '●' : '○'}{' '}
                {t(`admin.import.step_${k}`)}
              </span>
              {i < stepKeys.length - 1 && <span className="text-gray-300 text-[11px]">→</span>}
            </li>
          )
        })}
      </ol>

      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-orange-400 transition-all duration-500 animate-pulse"
          style={{
            width: `${Math.max(
              8,
              Math.round((view.processed_pages / Math.max(view.page_count, 1)) * 100),
            )}%`,
          }}
        />
      </div>

      {pages.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pages.map((p) => (
            <span
              key={p.n}
              title={t(`admin.import.page_${p.status}`)}
              className={
                p.status === 'ocr_done'
                  ? 'rounded-full bg-emerald-100 text-emerald-800 text-[11px] px-2 py-0.5 font-medium'
                  : p.status === 'error'
                    ? 'rounded-full bg-red-100 text-red-700 text-[11px] px-2 py-0.5 font-medium'
                    : 'rounded-full bg-orange-100 text-orange-800 text-[11px] px-2 py-0.5 font-medium animate-pulse'
              }
            >
              {p.status === 'ocr_done' ? '✓' : p.status === 'error' ? '✕' : '⏳'}{' '}
              {t('admin.import.page_chip', { n: String(p.n) })}
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500">
        {t('admin.import.page_progress', {
          n: String(view.processed_pages),
          total: String(view.page_count),
        })}
      </p>
      <p className="text-xs text-gray-400">{t('admin.import.outcome_hint_live')}</p>
    </Card>
  )
}
